import { useEffect, useState } from 'react';
import { layout } from 'voyager';

const TYPING_FIELD = [
  'input:not([type])',
  'input[type="text"]',
  'input[type="email"]',
  'input[type="tel"]',
  'input[type="number"]',
  'input[type="password"]',
  'input[type="search"]',
  'input[type="url"]',
  'textarea',
  '[contenteditable="true"]',
].join(', ');

const COARSE_POINTER = '(pointer: coarse)';

const desktopWidth = () => `(min-width: ${layout.desktopBreakpoint})`;

const isTouchPhone = () =>
  window.matchMedia(COARSE_POINTER).matches && !window.matchMedia(desktopWidth()).matches;

const opensKeyboard = (element: Element | null) =>
  element instanceof HTMLIFrameElement ||
  (element instanceof HTMLElement &&
    element.matches(TYPING_FIELD) &&
    !(element as HTMLInputElement).readOnly &&
    !(element as HTMLInputElement).disabled);

export const useIsKeyboardOpen = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const update = () => setIsOpen(isTouchPhone() && opensKeyboard(document.activeElement));
    const updateAfterFocusMoves = () => window.setTimeout(update, 0);
    const mediaQueries = [COARSE_POINTER, desktopWidth()].map((query) => window.matchMedia(query));

    document.addEventListener('focusin', update);
    document.addEventListener('focusout', updateAfterFocusMoves);
    window.addEventListener('blur', updateAfterFocusMoves);
    window.addEventListener('focus', updateAfterFocusMoves);
    mediaQueries.forEach((query) => query.addEventListener?.('change', update));
    return () => {
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', updateAfterFocusMoves);
      window.removeEventListener('blur', updateAfterFocusMoves);
      window.removeEventListener('focus', updateAfterFocusMoves);
      mediaQueries.forEach((query) => query.removeEventListener?.('change', update));
    };
  }, []);

  return isOpen;
};
